"use strict";

import React, { useMemo } from "react";
import { createRoot } from "react-dom/client";
import useInsightData from "./insight-dashboard/useInsightData";
import useInsightDashboardFilters from "./insight-dashboard/useInsightDashboardFilters";
import { Dashboard } from "./insight-dashboard/Dashboard";
import { YearSelect } from "./insight-dashboard/filters/YearSelect";
import { TimePeriodSelect } from "./insight-dashboard/filters/TimePeriodSelect";

import { iddeDashboardConfig } from "./insight-dashboard/iddeDashboardConfig";
import { sbmpDashboardConfig } from "./insight-dashboard/sbmpDashboardConfig";
import { muniSbmpDashboardConfig } from "./insight-dashboard/muniSbmpDashboardConfig";
import { lidSbmpDashboardConfig } from "./insight-dashboard/lidSbmpDashboardConfig";
import { peoDashboardConfig } from "./insight-dashboard/peoDashboardConfig";
import { DashboardConfig } from "../types/dashboardConfig";

interface InsightDashboardAppProps {
  config: DashboardConfig;
}

export const InsightDashboardApp: React.FC<InsightDashboardAppProps> = ({ config }) => {
  const { filters, setFilters, reportingYears, periodOptions, filterOptions, hasValidPeriods } =
    useInsightDashboardFilters(config);
  const {
    data: insightData,
    isLoading,
    isFetching,
    error,
  } = useInsightData(config.getInsightDataFunction, filters, hasValidPeriods);

  const widgetConfigs = useMemo(() => {
    if (!insightData) return [];
    return config.widgetConfigs.map((widgetConfig) => {
      const widgetData = widgetConfig.getData ? widgetConfig.getData(insightData) : {};
      return {
        ...widgetConfig,
        props: { ...widgetConfig.props, ...widgetData },
      };
    });
  }, [insightData, config.widgetConfigs]);

  if (isLoading) return <div></div>;
  if (error && !insightData) return <div>Error: {error.message}</div>;
  if (!insightData) return <div>No data available.</div>;

  return (
    <div className="tw:p-4 tw:relative tw:max-w-full tw:overflow-x-hidden">
      {isFetching && (
        <div className="tw:absolute tw:inset-0 tw:bg-white/50 tw:flex tw:items-center tw:justify-center tw:z-10">
          <div className="tw:text-muted-foreground">Updating...</div>
        </div>
      )}
      <div className="tw:flex tw:flex-wrap tw:gap-4 tw:mb-4">
        {config.timePeriodPicker && periodOptions && (
          <TimePeriodSelect
            reportingYears={periodOptions.reportingYears}
            rollingPeriods={periodOptions.rollingPeriods}
            timeZone={periodOptions.timeZone}
            focusPeriod={filters.focusPeriod}
            comparisonPeriod={filters.comparisonPeriod}
            onChange={(periods) => setFilters((prev) => ({ ...prev, ...periods }))}
          />
        )}
        {reportingYears.length > 0 && !config.timePeriodPicker && (
          <>
            <YearSelect
              label="Focus Year"
              value={filters.focusYear}
              options={reportingYears}
              onChange={(value) => setFilters((prev) => ({ ...prev, focusYear: Number(value) }))}
            />
            <YearSelect
              label="Comparison Year"
              value={filters.comparisonYear}
              options={reportingYears}
              onChange={(value) =>
                setFilters((prev) => ({ ...prev, comparisonYear: Number(value) }))
              }
            />
          </>
        )}

        {config.configurableFilters.map((filterConfig, index) => {
          const FilterComponent = filterConfig.component;
          return (
            <FilterComponent
              key={index}
              {...filterConfig.props}
              value={filters[filterConfig.stateKey]}
              options={
                filterConfig.getOptions?.(insightData) ?? filterConfig.options ?? filterOptions
              }
              onChange={(value: string | string[]) =>
                setFilters((prev) => ({
                  ...prev,
                  [filterConfig.stateKey]: value,
                }))
              }
            />
          );
        })}
      </div>
      <Dashboard data={insightData} config={widgetConfigs} />
    </div>
  );
};

export const NewInsightDashboardController = function (dashboardType: string) {
  var inited = false;
  var root: ReturnType<typeof createRoot> | null = null;

  var init = async function (reInitialize = false) {
    if (!inited || reInitialize) {
      const container = document.getElementById("react-insight-root");

      const dashboardConfigs: Record<string, DashboardConfig> = {
        idde: iddeDashboardConfig,
        bmp: sbmpDashboardConfig,
        "muni-bmp": muniSbmpDashboardConfig,
        "lid-bmp": lidSbmpDashboardConfig,
        peo: peoDashboardConfig,
      };

      const config = dashboardConfigs[dashboardType];

      if (!config) {
        console.error(`No dashboard configuration found for type: ${dashboardType}`);
        return;
      }

      if (container) {
        // Clear any legacy dashboard content that may still be visible
        const insightContainer = document.querySelector(".insight-container");
        if (insightContainer) {
          const dashboardContainer = insightContainer.querySelector(".dashboard-container");
          const filterContainer = insightContainer.querySelector(".insight-filter-container");
          const headingContainer = insightContainer.querySelector(".insight-heading-container");
          if (dashboardContainer) dashboardContainer.innerHTML = "";
          if (filterContainer) filterContainer.innerHTML = "";
          if (headingContainer) headingContainer.innerHTML = "";
        }

        // Unmount existing root if re-initializing, then create new root
        if (root) {
          root.unmount();
        }
        root = createRoot(container);
        root.render(<InsightDashboardApp config={config} />);
      }
      inited = true;
    }
  };

  return {
    init: init,
  };
};
